import { useState } from 'react';
import { useForm } from 'react-hook-form';
import emailjs from '@emailjs/browser';

const ContactForm = () => {
  const CONTACT_FORM_DISABLED = process.env.REACT_APP_DISABLE_CONTACT_FORM === 'true';
  const SUPPORT_EMAIL = 'info@choicestfruit.com';

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm();
  const [disabled, setDisabled] = useState(false);
  const [alertInfo, setAlertInfo] = useState({
    display: false,
    message: '',
    type: '',
  });

  // Shows alert message for form submission feedback
  const toggleAlert = (message, type) => {
    setAlertInfo({ display: true, message, type });

    // Hide alert after 5 seconds
    setTimeout(() => {
      setAlertInfo({ display: false, message: '', type: '' });
    }, 5000);
  };

  // Function called on submit that uses emailjs to send email of valid contact form
  const onSubmit = async (data) => {
    if (CONTACT_FORM_DISABLED) return;

    // Destrcture data object
    const { name, email, subject, message } = data;
    let didSend = false;
    try {
      // Disable form while processing submission
      setDisabled(true);

      // Define template params
      const templateParams = {
        name,
        email,
        subject,
        message,
      };

      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

      if (!serviceId || !templateId || !publicKey) {
        toggleAlert(
          "Email service isn't configured yet. Add REACT_APP_EMAILJS_SERVICE_ID, REACT_APP_EMAILJS_TEMPLATE_ID, and REACT_APP_EMAILJS_PUBLIC_KEY to .env.local, then restart the dev server.",
          'danger',
        );
        return;
      }

      // Use emailjs to email contact form data
      await emailjs.send(
        serviceId,
        templateId,
        templateParams,
        publicKey,
      );

      // Display success alert
      toggleAlert('Form submission was successful!', 'success');
      didSend = true;
    } catch (e) {
      console.error('EmailJS send failed:', e);

      const status = typeof e?.status === 'number' ? e.status : null;
      const text = typeof e?.text === 'string' ? e.text : null;
      const message = typeof e?.message === 'string' ? e.message : null;

      const combined = `${status ?? ''} ${text ?? ''} ${message ?? ''}`.toLowerCase();
      if (combined.includes('gmail_api') && combined.includes('invalid grant')) {
        toggleAlert(
          'Send failed — Email service needs reconnection (Gmail authorization expired). In EmailJS: Email Services → Gmail → Reconnect.',
          'danger',
        );
        return;
      }

      if (combined.includes('gmail_api') && combined.includes('insufficient') && combined.includes('scopes')) {
        toggleAlert(
          'Send failed — Gmail is connected without the required permissions (OAuth scopes). In EmailJS: Email Services → Gmail → Reconnect, then approve all requested permissions. If it persists: remove EmailJS access in your Google Account (Security → Third-party access) and reconnect.',
          'danger',
        );
        return;
      }

      const detail =
        (status && text && `${status}: ${text}`) ||
        (text && text) ||
        (message && message) ||
        'Unknown error';

      toggleAlert(`Send failed — ${detail}`, 'danger');
    } finally {
      // Re-enable form submission
      setDisabled(false);
      // Reset contact form fields only after success
      if (didSend) reset();
    }
  };

  const handleFormSubmit = CONTACT_FORM_DISABLED
    ? (e) => {
        e.preventDefault();

        // Clear only the message field so the user doesn't lose their contact details.
        setValue('message', '', { shouldDirty: false, shouldTouch: false });
        clearErrors();

        toggleAlert(
          `Messages can't be sent right now. Please email the company at ${SUPPORT_EMAIL}.`,
          'warning',
        );
      }
    : handleSubmit(onSubmit);

  return (
    <div className='ContactForm'>
      <form id='contact-form' onSubmit={handleFormSubmit} noValidate>
        <div className='row formRow'>
          <div className='col-12 col-md-6'>
            <input
              type='text'
              autoComplete='name'
              aria-invalid={errors.name ? 'true' : 'false'}
              {...register('name', {
                required: {
                  value: true,
                  message: 'Please enter your name',
                },
                maxLength: {
                  value: 30,
                  message: 'Please use 30 characters or less',
                },
              })}
              className='form-control formInput'
              placeholder='Name'
            />
            {!CONTACT_FORM_DISABLED && errors.name && (
              <span className='errorMessage'>{errors.name.message}</span>
            )}
          </div>

          <div className='col-12 col-md-6'>
            <input
              type='email'
              autoComplete='email'
              aria-invalid={errors.email ? 'true' : 'false'}
              {...register('email', {
                required: true,
                pattern:
                  /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
              })}
              className='form-control formInput'
              placeholder='Email address'
            />
            {!CONTACT_FORM_DISABLED && errors.email && (
              <span className='errorMessage'>Please enter a valid email address</span>
            )}
          </div>
        </div>

        <div className='row formRow'>
          <div className='col-12'>
            <input
              type='text'
              aria-invalid={errors.subject ? 'true' : 'false'}
              {...register('subject', {
                required: {
                  value: true,
                  message: 'Please enter a subject',
                },
                maxLength: {
                  value: 80,
                  message: 'Please use 80 characters or less',
                },
              })}
              className='form-control formInput'
              placeholder='Subject'
            />
            {!CONTACT_FORM_DISABLED && errors.subject && (
              <span className='errorMessage'>{errors.subject.message}</span>
            )}
          </div>
        </div>

        <div className='row formRow'>
          <div className='col-12'>
            <textarea
              rows={5}
              aria-invalid={errors.message ? 'true' : 'false'}
              {...register('message', {
                required: true,
              })}
              className='form-control formInput'
              placeholder='Message'
            ></textarea>
            {!CONTACT_FORM_DISABLED && errors.message && (
              <span className='errorMessage'>Please enter a message</span>
            )}
          </div>
        </div>

        <button
          className='submit-btn btn custom-yellow-btn'
          disabled={disabled}
          type='submit'
        >
          {disabled ? 'Sending…' : 'Send message'}
        </button>
        <p className='contact-form-helper'>Prefer email? Reach us directly at <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.</p>
      </form>
      {alertInfo.display && (
        <div
          className={`alert alert-${alertInfo.type} alert-dismissible mt-5`}
          role='alert'
          aria-live='polite'
        >
          {alertInfo.message}
          <button
            type='button'
            className='btn-close'
            data-bs-dismiss='alert'
            aria-label='Close'
            onClick={() =>
              setAlertInfo({ display: false, message: '', type: '' })
            } // Clear the alert when close button is clicked
          ></button>
        </div>
      )}
    </div>
  );
};

export default ContactForm;
